import { useMemo, useState } from 'react';
import type { JSX } from 'react';

import { ChevronDown, Search } from 'lucide-react';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';

import { useGetCredentialsQuery } from '../api/integrations';
import { ID_TEXT_CLASS } from '../lib/id';
import { formatJson } from '../lib/json';
import { PROVIDER_KEYS } from '../model/integration';
import type { IntegrationCredential } from '../model/integration';
import CredentialFormDialog from './CredentialFormDialog';

interface CredentialsSectionProps {
  /** Provider shown first: the selected integration's */
  initialProvider?: string;
  /** The selected integration's credential: named in the header and listed first */
  currentCredentialId?: number | null;
}

// The settings as text the search matches against, e.g. "baseUrl: https://sci.ia.cl"
const describeSettings = (settings: IntegrationCredential['settings']): string[] =>
  Object.entries(settings ?? {}).map(
    ([key, value]) => `${key}: ${typeof value === 'string' ? value : JSON.stringify(value)}`
  );

const matchesSearch = (credential: IntegrationCredential, search: string): boolean => {
  const normalized = search.trim().toLowerCase();
  if (!normalized) return true;
  return [credential.label, `#${credential.id}`, ...describeSettings(credential.settings)].some((text) =>
    text.toLowerCase().includes(normalized)
  );
};

/*
Credentials belong to no project, since several masters can share one: they are listed per provider. A provider can
have dozens (PlanOk, one per company), so the section starts closed and lists them with a search when opened.
*/
const CredentialsSection = ({ initialProvider, currentCredentialId = null }: CredentialsSectionProps): JSX.Element => {
  const [provider, setProvider] = useState(initialProvider ?? PROVIDER_KEYS[0]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<IntegrationCredential | 'new' | null>(null);
  const { data: credentials = [], isLoading, error } = useGetCredentialsQuery(provider);

  const currentCredential = credentials.find(({ id }) => id === currentCredentialId) ?? null;
  const visibleCredentials = useMemo(
    () =>
      credentials
        .filter((credential) => matchesSearch(credential, search))
        // The integration's own credential first, then by name
        .sort((a, b) => {
          const aIsCurrent = a.id === currentCredentialId;
          const bIsCurrent = b.id === currentCredentialId;
          if (aIsCurrent !== bIsCurrent) return aIsCurrent ? -1 : 1;
          return a.label.localeCompare(b.label);
        }),
    [credentials, search, currentCredentialId]
  );

  const changeProvider = (value: string): void => {
    setProvider(value);
    setSearch('');
  };

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="overflow-hidden rounded-xl border border-border-subtle bg-bg-surface"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border-subtle bg-bg-surface-raised px-4 py-3">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex min-w-0 items-center gap-2 text-left"
            aria-label="Mostrar u ocultar credenciales"
          >
            <ChevronDown className={cn('size-4 shrink-0 text-text-muted transition-transform', open && 'rotate-180')} />
            <span className="text-sm font-medium text-text-default">Inventario de credenciales</span>
            <Badge variant="secondary" className="text-[10px]">
              {provider} · {isLoading ? '…' : credentials.length}
            </Badge>
            {currentCredential ? (
              <span className="truncate text-xs text-text-muted">
                Esta integración usa {currentCredential.label}{' '}
                <span className={ID_TEXT_CLASS}>#{currentCredential.id}</span>
              </span>
            ) : null}
          </button>
        </CollapsibleTrigger>
        <div className="flex items-center gap-2">
          <Select value={provider} onValueChange={changeProvider}>
            <SelectTrigger size="sm" className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PROVIDER_KEYS.map((key) => (
                <SelectItem key={key} value={key}>
                  {key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={() => setEditing('new')}>
            Nueva credencial
          </Button>
        </div>
      </div>

      <CollapsibleContent className="flex flex-col gap-3 p-4">
        {isLoading ? <p className="m-0 text-sm text-text-muted">Cargando…</p> : null}
        {error ? (
          <p className="m-0 text-sm text-error">
            {extractErrorMessage(error, 'No se pudieron cargar las credenciales')}
          </p>
        ) : null}
        {!isLoading && !error && credentials.length === 0 ? (
          <p className="m-0 text-sm text-text-muted">Sin credenciales para {provider}.</p>
        ) : null}

        {credentials.length > 0 ? (
          <>
            <div className="relative max-w-sm">
              <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-text-muted" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar por nombre, #id o settings"
                className="h-8 pl-7 text-xs"
              />
            </div>

            {visibleCredentials.length === 0 ? (
              <p className="m-0 text-sm text-text-muted">Ninguna credencial coincide con la búsqueda.</p>
            ) : (
              // The table scrolls inside the section, so a long list does not push the rest of the page down
              <div className="max-h-[28rem] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">ID</TableHead>
                      <TableHead>Credencial</TableHead>
                      <TableHead>Settings</TableHead>
                      <TableHead>Secrets definidos</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleCredentials.map((credential) => (
                      <TableRow key={credential.id} className="align-top">
                        <TableCell className={ID_TEXT_CLASS}>{credential.id}</TableCell>
                        <TableCell>
                          {credential.label}
                          {credential.id === currentCredentialId ? (
                            <Badge variant="secondary" className="ml-2 text-[10px]">
                              En esta integración
                            </Badge>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <pre className="m-0 max-w-96 overflow-auto text-[11px]">
                            {formatJson(credential.settings)}
                          </pre>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {Object.keys(credential.secrets ?? {}).join(', ') || '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={() => setEditing(credential)}>
                            Editar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        ) : null}
      </CollapsibleContent>

      {editing ? (
        <CredentialFormDialog
          provider={provider}
          credential={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </Collapsible>
  );
};

export default CredentialsSection;
